import React from "react";

/**
 * Parse the build-time product selector.
 *
 * Empty input preserves the historical both-products mode -- chat is never
 * part of that default; it only loads when explicitly named, since it's a
 * standalone deploy (own origin/port), not something combined with
 * mantis/dataeng in one build.
 *
 * @param {string|undefined} productsEnv Comma-separated product names.
 * @returns {Set<string>} Enabled `mantis`/`dataeng`/`chat` products.
 * @example
 * parseEnabledProducts("mantis");
 * // Set(["mantis"])
 * @example
 * parseEnabledProducts("");
 * // Set(["mantis", "dataeng"])
 * @example
 * parseEnabledProducts("chat");
 * // Set(["chat"])
 */
export const parseEnabledProducts = (productsEnv) => {
  const products = String(productsEnv || "")
    .split(",")
    .map((product) => product.trim().toLowerCase())
    .filter(Boolean);
  const enabled = new Set(products.length ? products : ["mantis", "dataeng"]);
  const unsupported = [...enabled].filter(
    (product) => product !== "mantis" && product !== "dataeng" && product !== "chat"
  );
  if (unsupported.length) {
    throw new Error(`Unsupported REACT_APP_PRODUCTS: ${unsupported.join(", ")}`);
  }
  return enabled;
};

/**
 * Load only the selected product entry modules and compose shared route seams.
 *
 * @returns {Object} Product components, auth lifecycle, and presentation.
 * @example
 * // With REACT_APP_PRODUCTS=dataeng:
 * createProductComposition();
 * // { DataengProduct: Function, MantisProduct: null, ... }
 */
export const createProductComposition = () => {
  const productsEnv = process.env.REACT_APP_PRODUCTS;
  const enabled = parseEnabledProducts(productsEnv);
  let mantis = null;
  let dataeng = null;
  let chat = null;

  // Keep these checks directly against process.env: CRA's DefinePlugin can
  // eliminate the omitted require and its entire product dependency graph.
  // chat is always a standalone single-product build (REACT_APP_PRODUCTS
  // must literally equal "chat"), so it never needs the dataeng/mantis
  // exclusion dance those two do for each other.
  if (process.env.REACT_APP_PRODUCTS !== "dataeng" && process.env.REACT_APP_PRODUCTS !== "chat") {
    mantis = require("./mantis/product");
  }
  if (process.env.REACT_APP_PRODUCTS !== "mantis" && process.env.REACT_APP_PRODUCTS !== "chat") {
    dataeng = require("./dataeng/product");
  }
  if (process.env.REACT_APP_PRODUCTS === "chat") {
    chat = require("./chat/product");
  }

  if (!enabled.has("mantis")) mantis = null;
  if (!enabled.has("dataeng")) dataeng = null;
  if (!enabled.has("chat")) chat = null;

  let AddSourceRoute = dataeng?.AddSource || null;
  if (mantis && dataeng) {
    const ComposedAddSourceRoute = () => (
      <mantis.MantisAwareAddSourceRoute
        AddSourceComponent={dataeng.AddSource}
      />
    );
    AddSourceRoute = ComposedAddSourceRoute;
  }

  return {
    MantisProduct: mantis?.MantisProduct || null,
    DataengProduct: dataeng?.DataengProduct || null,
    ChatProduct: chat?.ChatProduct || null,
    AddSourceRoute,
    dataengAlternateBrand: mantis?.mantisNavBrand || null,
    AuthenticatedRedirect: mantis?.MantisAuthenticatedRedirect || null,
    Chrome: mantis?.MantisChrome || null,
    authLifecycle: mantis?.mantisAuthLifecycle || {},
    authPresentation:
      mantis?.mantisAuthPresentation ||
      dataeng?.dataengAuthPresentation ||
      chat?.chatAuthPresentation ||
      {},
  };
};
